import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { Z } from '../lib/zLayers'

// ── EdgeSwipeBack ─────────────────────────────────────────────────────────────
// iOS gives Safari an edge-swipe-back gesture but withholds it from installed
// PWAs — the single most-missed "typical mobile gesture" in the app. This
// restores it: drag right from the left screen edge (≤26px) on any DRILL-IN
// page and release past the threshold to go back. A chevron puck follows the
// drag and lights up when the release would fire.
//
// Deliberately inert on root tab pages (back there would walk out of the app)
// and when there's no history. Other swipe zones (ShowDetail hero, episode
// rows, MyList SwipeRow) ignore touches starting in the edge zone so gestures
// never double-fire.

const ROOT_PATHS = new Set(['/', '/discover', '/calendar', '/list', '/profile', '/for-you'])
export const EDGE_ZONE = 30   // px, touches starting here belong to back-swipe
const THRESHOLD = 72          // px of rightward drag to commit

export function EdgeSwipeBack() {
  const navigate = useNavigate()
  const location = useLocation()
  const [drag, setDrag] = useState(0)
  const dragRef  = useRef(0)
  const tracking = useRef(false)
  const start    = useRef({ x: 0, y: 0 })
  const enabled  = !ROOT_PATHS.has(location.pathname)

  useEffect(() => {
    if (!enabled) return
    const setDragBoth = (v) => { dragRef.current = v; setDrag(v) }

    const onStart = (e) => {
      const t = e.touches[0]
      if (t.clientX > EDGE_ZONE) return
      tracking.current = true
      start.current = { x: t.clientX, y: t.clientY }
    }
    const onMove = (e) => {
      if (!tracking.current) return
      const t = e.touches[0]
      const dx = t.clientX - start.current.x
      const dy = Math.abs(t.clientY - start.current.y)
      // Mostly-vertical drag = a scroll, not a back gesture
      if (dy > 50 && dy > dx) { tracking.current = false; setDragBoth(0); return }
      setDragBoth(Math.max(0, Math.min(dx, 130)))
    }
    const onEnd = () => {
      if (tracking.current && dragRef.current >= THRESHOLD && window.history.length > 1) {
        navigate(-1)
      }
      tracking.current = false
      setDragBoth(0)
    }

    document.addEventListener('touchstart', onStart, { passive: true })
    document.addEventListener('touchmove',  onMove,  { passive: true })
    document.addEventListener('touchend',   onEnd,   { passive: true })
    document.addEventListener('touchcancel', onEnd,  { passive: true })
    return () => {
      document.removeEventListener('touchstart', onStart)
      document.removeEventListener('touchmove',  onMove)
      document.removeEventListener('touchend',   onEnd)
      document.removeEventListener('touchcancel', onEnd)
    }
  }, [enabled, navigate])

  if (!enabled || drag <= 0) return null
  const armed = drag >= THRESHOLD
  return (
    <div style={{
      position:'fixed', left:0, top:'50%', zIndex:Z.system,
      transform:`translate(${Math.min(drag * 0.55, 64) - 44}px, -50%)`,
      pointerEvents:'none',
      width:44, height:44, borderRadius:'var(--radius-full)',
      background: armed ? 'var(--amber)' : 'var(--surface-strong)',
      border:'1px solid rgba(var(--ink-rgb),0.2)',
      display:'flex', alignItems:'center', justifyContent:'center',
      boxShadow:'0 4px 18px rgba(0,0,0,0.5)',
      opacity: Math.min(1, drag / THRESHOLD),
      transition:'background 0.12s ease',
    }}>
      <ChevronLeft size={22} color={armed ? 'var(--amber-ink)' : 'var(--text-strong)'} strokeWidth={3}/>
    </div>
  )
}
